/**
 * CR500 Phase 5A: DetailsActionsBlock
 * CR500 Phase 5B: V1 parity - same button order, no duplicates, correct placement
 * CR500 Phase 6A: Telemetry integration
 * 
 * Renders action buttons on the details surface (View Deal, Claim, T&Cs).
 * This is a specialized version of ActionsBlock for the details context,
 * matching V1 parity for the back-face actions.
 * 
 * V1 parity rules:
 * - Primary CTA ("Claim deal") always present when expected
 * - Secondary actions only when relevant (View deal, T&Cs)
 * - No duplicate actions between front-face and details
 * - Link targets use same fields as V1
 */

import React from "react";
import { ExternalLink, FileText, Sparkles } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers/debugGuard";
import { trackCtaEvent } from "@/lib/deal-cards/telemetry";

export const DetailsActionsBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  const debugMode = isV2DebugEnabled();
  
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // V1 parity: same fields as ActionsSection in DealDetailsContent
  const proofUrl = deal.proof_urls?.[0] || null;
  const hasProof = !!proofUrl;
  
  // Terms URL: terms_url takes priority, then source_url
  const termsUrl = deal.terms_url || null;
  const hasTerms = !!termsUrl;
  
  // Claim URL priority chain: claim_url → deal_url → source_url (V1 parity)
  const claimUrl = deal.claim_url || deal.deal_url || deal.source_url;
  const hasClaimUrl = Boolean(claimUrl);

  // If no actions to show, don't render (match V1 behavior)
  if (!hasProof && !hasTerms && !hasClaimUrl) {
    if (debugMode) {
      return (
        <div 
          className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs"
          data-block="details_actions"
          data-block-id={block.id}
        >
          <span className="font-medium text-yellow-600">[Debug] DetailsActionsBlock: No actions</span>
          <div className="text-yellow-600/80 mt-1">
            Checked: proof_urls[0], terms_url, claim_url, deal_url, source_url
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div 
      className="space-y-2"
      data-block="details_actions"
      data-block-id={block.id}
    >
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" />
        {context.labels?.actions_title || "Actions"}
      </h4>
      <div className="flex flex-wrap gap-2">
        {/* Primary: View deal (proof URL) - V1 shows this first */}
        {hasProof && (
          <a
            href={proofUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              trackCtaEvent("view", "v2", deal.deal_type);
            }}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            View Deal <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        )}
        
        {/* Claim deal button - V1 shows as primary action */}
        {hasClaimUrl ? (
          <a
            href={claimUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              trackCtaEvent("claim", "v2", deal.deal_type);
            }}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Claim Deal <ExternalLink className="w-3 h-3 ml-1" />
          </a>
        ) : (
          <span 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 bg-muted text-muted-foreground cursor-not-allowed" 
            title="No claim link available"
          >
            Claim Deal
          </span>
        )}
        
        {/* T&Cs link - V1 shows only if terms_url exists AND not already shown via claim */}
        {hasTerms && (
          <a
            href={termsUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              trackCtaEvent("terms", "v2", deal.deal_type);
            }}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 px-3 border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <FileText className="w-3 h-3 mr-1" />
            T&Cs
          </a>
        )}
      </div>
      
      {/* Debug info */}
      {debugMode && (
        <div className="text-[10px] text-muted-foreground/50 border-t border-border/50 pt-1 mt-1">
          proofUrl: {hasProof ? 'present' : 'null'} | 
          claimUrl: {claimUrl ? (deal.claim_url ? 'claim_url' : deal.deal_url ? 'deal_url' : 'source_url') : 'null'} | 
          termsUrl: {hasTerms ? 'present' : 'null'}
        </div>
      )}
    </div>
  );
};

export default DetailsActionsBlock;
